<script setup lang="ts" generic="T extends { id: string | number; label: string }">
import { nextTick, ref, watch } from 'vue'
import { trans } from '@/munio/i18n'
// This is a wrapper for vue-treeselect
// https://vue-treeselect.js.org/#api
import TreeSelect from '@/libs/vue-treeselect'

defineOptions({
  name: 'TreeSelect',
  inheritAttrs: false,
})

type OptionProps = {
  node: {
    id: string | number
    label: string
    isLeaf: boolean
    isDisabled: boolean
    raw: T
  }
  labelClassName: string
}

type BaseProps = {
  name?: string
  clearAllText?: string
  clearValueText?: string
  limitText?: (count: number) => string
  loadingText?: string
  noChildrenText?: string
  noOptionsText?: string
  noResultsText?: string
  placeholder?: string
  retryText?: string
  retryTitle?: string
  searchPromptText?: string
  searchable?: boolean
  clearable?: boolean
  multiple?: boolean
  disabled?: boolean
  required?: boolean
  tabindex?: string | number
  autofocus?: boolean
  options?: T[]
  alwaysOpen?: boolean
  displayOnly?: boolean
  appendToBody?: boolean
  defaultExpandLevel?: number
  disableBranchNodes?: boolean
  disableFuzzyMatching?: boolean
  normalizer?: (option: T) => T
  searchQuery?: string
  overrideIsDisabled?: boolean
}

type Props =
  | (BaseProps & {
      valueFormat?: 'id'
      multiple?: false
      modelValue?: T['id']
    })
  | (BaseProps & {
      valueFormat?: 'id'
      multiple: true
      modelValue?: T['id'][]
    })
  | (BaseProps & {
      valueFormat: 'object'
      multiple?: false
      modelValue?: T
    })
  | (BaseProps & {
      valueFormat: 'object'
      multiple: true
      modelValue?: T[]
    })

const props = withDefaults(defineProps<Props>(), {
  clearAllText: trans('Clear all'),
  clearValueText: trans('Clear'),
  limitText: (count: number) => 'and '.concat(String(count), ' more'),
  loadingText: trans('Loading'),
  noChildrenText: trans('No options'),
  noOptionsText: trans('No options'),
  noResultsText: trans('No entries found'),
  placeholder: trans('Select'),
  retryText: `${trans('Retry')}?`,
  retryTitle: trans('Retry'),
  searchPromptText: trans('Search'),
  defaultExpandLevel: 0,
  disableFuzzyMatching: true,
  valueFormat: 'id',
})

const emit = defineEmits(['update:modelValue', 'search-change', 'close', 'open', 'select', 'deselect'])

const slots = defineSlots<{
  'option-label'(props: OptionProps): void
  'value-label'(props: OptionProps): void
  'before-list'(): void
  'after-list'(): void
}>()

const $select = ref<typeof TreeSelect & { getMenu(): HTMLDivElement }>()

watch(
  () => props.searchQuery,
  () => {
    nextTick(() => {
      if ($select.value) {
        $select.value.setSearchQuery(props.searchQuery)
      }
    })
  },
)

function onOpen(...args: T[]) {
  emit('open', ...args)

  if (props.displayOnly) {
    return
  }
}
</script>

<template>
  <TreeSelect
    ref="$select"
    v-bind="$attrs"
    :name="name"
    :value-format="valueFormat"
    :model-value="modelValue"
    :tab-index="tabindex !== undefined ? Number(tabindex) : undefined"
    :clear-all-text="clearAllText"
    :clear-value-text="clearValueText"
    :limit-text="limitText"
    :loading-text="loadingText"
    :no-children-text="noChildrenText"
    :no-options-text="noOptionsText"
    :no-results-text="noResultsText"
    :placeholder="placeholder"
    :retry-text="retryText"
    :retry-title="retryTitle"
    :search-prompt-text="searchPromptText"
    :searchable="searchable"
    :clearable="clearable"
    :multiple="multiple"
    :disabled="disabled"
    :required="required"
    :options="options"
    :always-open="alwaysOpen || displayOnly"
    :default-expand-level="defaultExpandLevel"
    :class="{ 'display-only': displayOnly }"
    :append-to-body="appendToBody"
    :disable-branch-nodes="disableBranchNodes"
    :disable-fuzzy-matching="disableFuzzyMatching"
    :auto-focus="autofocus"
    :normalizer="normalizer"
    :override-is-disabled="overrideIsDisabled"
    @open="onOpen"
    @close="(...args) => $emit('close', ...args)"
    @update:model-value="(...args) => $emit('update:modelValue', ...args)"
    @search-change="(...args) => $emit('search-change', ...args)"
    @select="(...args) => $emit('select', ...args)"
    @deselect="(...args) => $emit('deselect', ...args)"
  >
    <template v-if="slots['option-label']" #option-label="optionProps: OptionProps">
      <slot name="option-label" v-bind="optionProps" />
    </template>
    <template v-if="slots['value-label']" #value-label="valueProps: OptionProps">
      <slot name="value-label" v-bind="valueProps" />
    </template>
    <template v-if="slots['before-list']" #before-list>
      <slot name="before-list" />
    </template>
    <template v-if="slots['after-list']" #after-list>
      <slot name="after-list" />
    </template>
  </TreeSelect>
</template>
